"use client";

import {
  Button,
  LoadingOverlay,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { EmptyState, Icon } from "@repo/dashboard-core";
import type { StorageFile } from "@/lib/types";
import dayjs from "dayjs";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { fetchFiles, getFileDownloadUrl } from "@/lib/actions/files";
import { displayName, truncateName } from "@/lib/utils/file-helpers";

import { DeleteButton } from "./DeleteButton";
import { DownloadButton } from "./DownloadButton";

interface FilesTableProps {
  /** Expose the refresh callback to the parent (e.g. UploadCta). */
  registerReload?: (reload: () => Promise<void>) => void;
  /** "ai" shows an AI Chat link per file instead of download/delete/preview buttons. */
  variant?: "default" | "ai";
  /** Called when user clicks preview or download. Returns true if feedback was triggered (caller should defer its action). */
  onFileAction?: (type: "preview" | "download", fileName: string) => boolean;
}

export function FilesTable({
  registerReload,
  variant = "default",
  onFileAction,
}: FilesTableProps) {
  const t = useTranslations("my-files");
  const router = useRouter();
  const locale = useLocale();

  const [files, setFiles] = useState<StorageFile[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    const result = await fetchFiles();
    setFiles(result.success ? result.data : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
    registerReload?.(reload);
  }, [reload, registerReload]);

  const handlePreview = useCallback(
    async (name: string) => {
      try {
        const result = await getFileDownloadUrl(name);
        if (!result.success) throw new Error(result.message);
        router.push(`/${locale}/preview?name=${encodeURIComponent(name)}`);
      } catch (err) {
        notifications.show({
          title: t("error"),
          message: err instanceof Error ? err.message : t("preview-failed"),
          color: "red",
        });
      }
    },
    [locale, router, t],
  );

  const handlePreviewClick = useCallback(
    (name: string) => {
      const triggered = onFileAction?.("preview", name) ?? false;
      if (!triggered) handlePreview(name);
    },
    [onFileAction, handlePreview],
  );

  const handleDownloadClick = useCallback(
    (name: string) => {
      return onFileAction?.("download", name) ?? false;
    },
    [onFileAction],
  );

  if (!loading && files.length === 0) {
    return (
      <EmptyState
        icon={<Icon name="fileText" size={32} />}
        title={t("empty.title")}
        description={t("empty.description")}
      />
    );
  }

  return (
    <div className="relative">
      <LoadingOverlay
        visible={loading}
        overlayProps={{ blur: 2, backgroundOpacity: 0 }}
        zIndex={1}
      />
      <Table striped verticalSpacing="sm">
        <TableThead className="hidden md:table-header-group">
          <TableTr>
            <TableTh>{t("table.file-name")}</TableTh>
            <TableTh>{t("table.file-type")}</TableTh>
            <TableTh>{t("table.created-at")}</TableTh>
            <TableTh className="text-right">
              <span className="sr-only">{t("table.actions")}</span>
            </TableTh>
          </TableTr>
        </TableThead>
        <TableTbody>
          {files.map(({ name, extension, modified }) => {
            const full = displayName(name);
            const truncated = truncateName(full, 50);
            const date = dayjs(modified).format("DD/MM/YYYY HH:mm");

            return (
              <TableTr
                key={name}
                data-testid="my-files-row"
                className="md:table-row flex flex-col"
              >
                {/* Mobile: card layout */}
                <TableTd className="md:hidden" colSpan={4}>
                  <div className="space-y-3">
                    {/* Row 1: File name */}
                    <div>
                      <p className="text-xs font-extrabold text-gray-500">
                        {t("table.file-name")}
                      </p>
                      <p className="break-all" title={full}>
                        {truncated}
                      </p>
                    </div>
                    {/* Row 2: File type + Date side by side */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-extrabold text-gray-500">
                          {t("table.file-type")}
                        </p>
                        <p className="uppercase">{extension}</p>
                      </div>
                      <div>
                        <p className="text-xs font-extrabold text-gray-500">
                          {t("table.created-at")}
                        </p>
                        <p>{date}</p>
                      </div>
                    </div>
                    {/* Row 3: Buttons */}
                    {variant === "ai" ? (
                      extension === "pdf" && (
                        <Button
                          size="xs"
                          fullWidth
                          component="a"
                          href={`/${locale}/ai-chat/${encodeURIComponent(name)}`}
                        >
                          {t("ai-button")}
                        </Button>
                      )
                    ) : (
                      <div className="flex gap-2">
                        {extension === "pdf" && (
                          <Button
                            variant="outline"
                            color="gray"
                            size="xs"
                            className="hover:border-[var(--mantine-color-orange-filled)] hover:text-[var(--mantine-color-orange-filled)] flex-1"
                            leftSection={<Icon name="eye" size={14} />}
                            onClick={() => handlePreviewClick(name)}
                          >
                            {t("table.preview")}
                          </Button>
                        )}
                        {extension === "pdf" && (
                          <Button
                            variant="outline"
                            color="gray"
                            size="xs"
                            className="hover:border-[var(--mantine-color-orange-filled)] hover:text-[var(--mantine-color-orange-filled)] flex-1"
                            leftSection={<Icon name="edit" size={14} />}
                            onClick={() =>
                              router.push(
                                `/${locale}/editor?name=${encodeURIComponent(name)}`,
                              )
                            }
                          >
                            {t("table.edit")}
                          </Button>
                        )}
                        <DownloadButton
                          fileName={name}
                          onDownloadClick={() => handleDownloadClick(name)}
                        />
                        <DeleteButton fileName={name} onDeleted={reload} />
                      </div>
                    )}
                  </div>
                </TableTd>

                {/* Desktop: normal table columns */}
                <TableTd className="hidden md:table-cell">
                  <span className="break-all" title={full}>
                    {truncated}
                  </span>
                </TableTd>
                <TableTd className="hidden uppercase md:table-cell">
                  {extension}
                </TableTd>
                <TableTd className="hidden md:table-cell">{date}</TableTd>
                <TableTd className="hidden md:table-cell">
                  {variant === "ai" ? (
                    <div className="flex justify-end">
                      {extension === "pdf" && (
                        <Button
                          size="xs"
                          component="a"
                          href={`/${locale}/ai-chat/${encodeURIComponent(name)}`}
                        >
                          {t("ai-button")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="flex justify-end gap-2">
                      {extension === "pdf" && (
                        <Button
                          variant="outline"
                          color="gray"
                          size="xs"
                          className="hover:border-[var(--mantine-color-orange-filled)] hover:text-[var(--mantine-color-orange-filled)]"
                          leftSection={<Icon name="eye" size={14} />}
                          onClick={() => handlePreviewClick(name)}
                        >
                          {t("table.preview")}
                        </Button>
                      )}
                      {extension === "pdf" && (
                        <Button
                          variant="outline"
                          color="gray"
                          size="xs"
                          className="hover:border-[var(--mantine-color-orange-filled)] hover:text-[var(--mantine-color-orange-filled)]"
                          leftSection={<Icon name="edit" size={14} />}
                          onClick={() =>
                            router.push(
                              `/${locale}/editor?name=${encodeURIComponent(name)}`,
                            )
                          }
                        >
                          {t("table.edit")}
                        </Button>
                      )}
                      <DownloadButton
                        fileName={name}
                        onDownloadClick={() => handleDownloadClick(name)}
                      />
                      <DeleteButton fileName={name} onDeleted={reload} />
                    </div>
                  )}
                </TableTd>
              </TableTr>
            );
          })}
        </TableTbody>
      </Table>
    </div>
  );
}
